import React from "react";

import { Link } from "react-router-dom";
import classNames from "classnames";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

import { SortDirection } from "types/types";

import { formatMs, formatNumber, formatPercent } from "utils/format";

import styles from "./style.module.scss";
import { useRoutes } from "utils/routes";
import LazyLoader from "ui/LazyLoader";
import Grid from "ui/Grid";
import Popover from "ui/Popover";
import { GridColumn, SortOrder } from "ui/Grid/util";

interface QueryType {
  id: string;
  queryId?: string;
  databaseId?: string;
  server?: {
    id: string;
    humanId: string;
    name: string;
  };
  serverName?: string;
  postgresRole?: null | {
    id: string;
    name: string;
  };
  postgresRoleName?: string;
  postgresRoleCount: number;
  truncatedQuery: string;
  normalizedQuery: string;
  avgTime: number;
  avgTimeDelta?: number;
  callsPerMinute: number;
  callsPerMinuteDelta?: number;
  bufferHitRatio?: number | null;
  pctOfTotal: number;
  pctOfTotalIo?: number | null;
  databaseCount?: number | null;
  representativeAvgTime?: number | null;
  isOutlier?: boolean | null;
  medianAvgTime?: number | null;
}

export type QueryStatsTableSortBy = keyof Omit<QueryType, "postgresRole">;

interface SortOptsType {
  sortBy: QueryStatsTableSortBy;
  sortDirection: SortDirection;
}

type BaseProps = {
  queries: QueryType[];
  searchTerm: null | string;
  compare: boolean;
  showingTable?: boolean;
  setSortOpts: (opts: SortOptsType) => void;
  sortOpts: SortOptsType;
  fetchMoreData: (loadedCount: number) => Promise<boolean>;
  // Rendered above the table, inside the grid's column context, and kept mounted
  // across loading/error states (so a search box here doesn't lose focus on
  // refetch). loading/error/noRowsText control the body below the header.
  header?: React.ReactNode;
  loading?: boolean;
  error?: boolean;
  noRowsText?: React.ReactNode;
};

type Props = BaseProps &
  (
    | {
        showServer: true;
        merged?: boolean;
        databaseId?: undefined;
        serverId?: undefined;
      }
    // serverId is optional so callers can render the table (header + a loading
    // body) before the fetch that provides it has resolved.
    | {
        showServer?: false;
        merged?: false;
        databaseId: string;
        serverId?: string;
      }
  );

function formatRatio(ratio: number): string {
  return `${formatNumber(ratio, ratio >= 10 ? 0 : 2)}×`;
}

// Builds the columns for a query-statistics grid.
function useQueryStatsColumns({
  databaseId,
  serverId,
  compare,
  showServer,
  merged,
  showingTable,
}: {
  databaseId?: string;
  serverId?: string;
  compare: boolean;
  showServer?: boolean;
  merged?: boolean;
  showingTable?: boolean;
}): GridColumn<QueryType, keyof QueryType>[] {
  const { databaseQuery, serverRole, server: serverRoute } = useRoutes();

  const queryColWidth = compare ? "1fr" : "4fr";
  const numColWidth = compare ? "10%" : "12%";
  return [
    {
      field: "normalizedQuery",
      header: "Query",
      width: queryColWidth,
      style: "query",
      disableSort: true,
      renderer: function QueryCell({ fieldData, rowData }) {
        const href = databaseQuery(
          rowData.databaseId ?? databaseId,
          rowData.queryId ?? rowData.id,
        );
        return (
          <Link to={href} title={fieldData}>
            {rowData.truncatedQuery}
          </Link>
        );
      },
    },
    {
      field: "postgresRoleName",
      header: "Role",
      width: merged
        ? false
        : showServer
        ? "minmax(10%,160px)"
        : "minmax(10%,120px)",
      nullValue: "n/a",
      disableSort: true,
      renderer: function RoleCell({ fieldData, rowData }) {
        const roleServerId = rowData.server?.humanId ?? serverId;
        return (
          <>
            <Link to={serverRole(roleServerId, rowData.postgresRole.id)}>
              {fieldData}
            </Link>{" "}
            {rowData.postgresRoleCount > 1 && (
              <span>+{rowData.postgresRoleCount - 1}</span>
            )}
          </>
        );
      },
    },
    {
      field: "serverName",
      header: merged ? "Runs on" : "Server",
      width: merged
        ? "minmax(140px,12%)"
        : showServer
        ? "minmax(10%,120px)"
        : false,
      nullValue: "n/a",
      disableSort: true,
      renderer: function ServerCell({ fieldData, rowData }) {
        if (merged && rowData.server && rowData.databaseCount > 1) {
          return (
            <div className="flex">
              <Link
                to={serverRoute(rowData.server.humanId)}
                title={fieldData}
                className="truncate"
              >
                {fieldData}
              </Link>
              <span className="shrink-0 ml-1 text-[#606060]">
                +{rowData.databaseCount - 1}
              </span>
            </div>
          );
        }
        if (!rowData.server) {
          return <>{fieldData}</>;
        }
        return (
          <Link to={serverRoute(rowData.server.humanId)}>{fieldData}</Link>
        );
      },
    },
    {
      field: "avgTime",
      header: merged ? "Avg Time" : "Avg Time (ms)",
      width: merged ? "minmax(90px,9%)" : numColWidth,
      style: "number",
      defaultSortOrder: "DESC",
      renderer: function AvgTimeCell({ fieldData }) {
        return formatMs(fieldData);
      },
    },
    {
      field: "avgTimeDelta",
      header: "7 Day Trend",
      width: compare ? "10%" : false,
      style: "number",
      defaultSortOrder: "DESC",
      nullValue: "n/a",
      renderer: AvgTimeTrendCell,
    },
    {
      field: "representativeAvgTime",
      header: "Slowest DB",
      tip: "The slowest database with at least 10% of the average calls per database. Red when it is over 3× the median of the other databases.",
      width: merged ? "minmax(110px,10%)" : false,
      style: "number",
      defaultSortOrder: "DESC",
      nullValue: "n/a",
      renderer: function SlowestDbCell({ fieldData, rowData }) {
        const ratio = fieldData / rowData.medianAvgTime;
        if (!(rowData.medianAvgTime > 0) || Number(ratio.toFixed(1)) <= 1) {
          return formatMs(fieldData);
        }
        return (
          <Popover
            popupClassName="font-sans"
            content={
              <>
                This database is <strong>{formatRatio(ratio)} slower</strong>{" "}
                than the median of the others ({formatMs(rowData.medianAvgTime)}
                ).
              </>
            }
          >
            <span className={rowData.isOutlier ? styles.deltaUp : undefined}>
              {formatMs(fieldData)}
              {rowData.isOutlier && <VisuallyHidden> outlier</VisuallyHidden>}
            </span>
          </Popover>
        );
      },
    },
    {
      field: "callsPerMinute",
      header: "Calls / min",
      width: merged ? "minmax(95px,9%)" : numColWidth,
      style: "number",
      defaultSortOrder: "DESC",
      renderer: function CallsPerMinCell({ fieldData }) {
        return fieldData.toFixed(2);
      },
    },
    {
      field: "callsPerMinuteDelta",
      header: "7 Day Trend",
      width: compare ? "10%" : false,
      style: "number",
      defaultSortOrder: "DESC",
      nullValue: "n/a",
      renderer: CallsTrendCell,
    },
    {
      field: "pctOfTotalIo",
      header: merged ? "% I/O" : "% of All I/O",
      width: merged ? "minmax(80px,8%)" : !showingTable ? numColWidth : false,
      style: "number",
      defaultSortOrder: "DESC",
      nullValue: "n/a",
      renderer: function PctOfTotalIoCell({ fieldData }) {
        return formatPercent(fieldData / 100.0);
      },
    },
    {
      field: "pctOfTotal",
      header: merged ? "% Runtime" : "% of All Runtime",
      width: merged ? "minmax(120px,11%)" : numColWidth,
      style: "number",
      defaultSortOrder: "DESC",
      renderer: function PctOfTotalCell({ fieldData }) {
        return formatPercent(fieldData / 100.0);
      },
    },
  ];
}

// Need to turn this into a data loading for the query statistics, similar to
// SchemaTableList - however this is more complicated because this table is
// used twice, once for the query overview, and once for the per-table queries
const QueryStatsTable = ({
  databaseId,
  serverId,
  compare,
  queries,
  showingTable,
  showServer,
  merged,
  sortOpts,
  setSortOpts,
  fetchMoreData,
  header,
  loading,
  error,
  noRowsText = "No queries matched",
}: Props) => {
  const columns = useQueryStatsColumns({
    databaseId,
    serverId,
    compare,
    showServer,
    merged,
    showingTable,
  });

  const queriesWithRoles: Array<QueryType> = queries.map(
    (q: QueryType): QueryType => {
      return {
        postgresRoleName: q.postgresRole?.name ?? null,
        serverName: q.server?.name ?? null,
        ...q,
      };
    },
  );

  function handleSort(sortBy: QueryStatsTableSortBy, sortOrder: SortOrder) {
    setSortOpts({
      sortBy,
      sortDirection: sortOrder,
    });
  }

  const sortedOrder = sortOpts.sortDirection;
  const sortedBy = sortOpts.sortBy as keyof QueryType;

  // Don't lazy-load more rows while the grid is showing a loading spinner or an
  // error instead of rows; returning `true` leaves the loader armed for once
  // real rows render.
  const loadMore = (loadedCount: number) =>
    loading || error ? Promise.resolve(true) : fetchMoreData(loadedCount);

  return (
    <LazyLoader loadedCount={queriesWithRoles.length} loadMore={loadMore}>
      <Grid
        striped
        header={header}
        loading={loading}
        error={error}
        data={queriesWithRoles}
        columns={columns}
        handleSort={handleSort}
        sortedBy={sortedBy}
        sortedOrder={sortedOrder}
        noRowsText={noRowsText}
      />
    </LazyLoader>
  );
};

function AvgTimeTrendCell({
  fieldData,
  rowData,
}: {
  fieldData: number;
  rowData: { avgTime: number };
}) {
  const delta = fieldData;
  const { avgTime: value } = rowData;

  const prevValue = value - delta;
  const pctDelta = (value - prevValue) / prevValue;
  if (delta === 0) {
    return "(no change)";
  }
  const sign = delta > 0 ? "+" : "";
  const pctChange = sign + formatPercent(pctDelta);
  const absChange = sign + formatMs(delta);
  return (
    <span
      title={pctChange}
      className={classNames(
        delta < 0 && styles.deltaDown,
        delta > 0 && styles.deltaUp,
      )}
    >
      {absChange}
    </span>
  );
}

function CallsTrendCell({
  rowData,
  fieldData,
}: {
  rowData: {
    callsPerMinute: number;
  };
  fieldData: number;
}) {
  const { callsPerMinute } = rowData;

  const delta = fieldData;
  if (delta === 0) {
    return "(no change)";
  }

  const value = callsPerMinute;

  const prevValue = value - delta;
  const pctDelta = (value - prevValue) / prevValue;

  // We only need to add the positive sign; the negative sign is part of the
  // formatted number
  const sign = delta > 0 ? "+" : "";
  const pctChange = sign + formatPercent(pctDelta);
  const absChange = sign + delta.toFixed(2);
  return (
    <span
      title={pctChange}
      className={classNames(
        delta < 0 && styles.deltaDown,
        delta > 0 && styles.deltaUp,
      )}
    >
      {absChange}
    </span>
  );
}

export default QueryStatsTable;
